import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Card } from 'antd';
import classNames from 'classnames';
import {
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_DESCRIPTION,
  QUESTION_NPS,
  QUESTION_RATING,
  QUESTION_RANKING,
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERIC_OPEN_ENDED,
  QUESTION_SLIDER,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_VAN_WESTENDORP,
} from 'data/nodeTypes';
import { isVanWestendorpQuestionNode } from 'utils/nodeTypes';
import MatrixQuestionViewOnly from 'containers/TemplatePreviewPage/QuestionsListViewOnly/GenericQuestionViewOnly/MatrixQuestionViewOnly';
import SingleMultipleQuestionViewOnly from 'containers/TemplatePreviewPage/QuestionsListViewOnly/GenericQuestionViewOnly/SingleMultipleQuestionViewOnly';
import PromptQuestionViewOnly from 'containers/TemplatePreviewPage/QuestionsListViewOnly/GenericQuestionViewOnly/PromptQuestionViewOnly';
import SliderQuestionViewOnly from 'containers/TemplatePreviewPage/QuestionsListViewOnly/GenericQuestionViewOnly/SliderQuestionViewOnly';
import { getABGroupAttributesForQuestion } from 'containers/QuestionnairePage/utils';
import { QUESTION_ICON_MAP } from './constants';
import { hasMention, getUpdatedPrompt } from './utils';

const GenericQuestionViewOnly = (props) => {
  const {
    question = {},
    nodes = [],
    screeningQuestions = [],
    hideActions,
  } = props;
  const { questionIndex } = question;

  const renderPrompt = useCallback(() => {
    let updatedPrompt = question.htmlPrompt || question.prompt;
    let promptClasses = '';
    if (question.answers && question.answers.length > 0) {
      promptClasses += 'margin-btm-xxs';
    }

    if (hasMention(question)) {
      updatedPrompt = getUpdatedPrompt({
        prompt: updatedPrompt,
        nodes,
        screeningQuestions,
      });
    }

    return (
      <span
        className={promptClasses}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: updatedPrompt }}
      />
    );
  }, [question]);

  const renderQuestion = (currentQuestion, allQuestions) => {
    const attributes = getABGroupAttributesForQuestion(question, allQuestions);
    const hideSingleMultipleActions =
      isVanWestendorpQuestionNode(currentQuestion) || hideActions;

    switch (currentQuestion.type) {
      case QUESTION_SINGLE:
      case QUESTION_MULTIPLE:
      case QUESTION_VAN_WESTENDORP:
      case QUESTION_RANKING: {
        return (
          <SingleMultipleQuestionViewOnly
            question={currentQuestion}
            renderPrompt={renderPrompt}
            attributes={attributes}
            hideActions={hideSingleMultipleActions}
          />
        );
      }
      case QUESTION_OPEN_ENDED:
      case QUESTION_NUMERIC_OPEN_ENDED:
      case QUESTION_DESCRIPTION:
      case QUESTION_RATING:
      case QUESTION_NPS: {
        return (
          <PromptQuestionViewOnly
            question={currentQuestion}
            icon={QUESTION_ICON_MAP[currentQuestion.type]}
            renderPrompt={renderPrompt}
            attributes={attributes}
          />
        );
      }
      case QUESTION_SLIDER: {
        return (
          <SliderQuestionViewOnly
            question={currentQuestion}
            icon={QUESTION_ICON_MAP[currentQuestion.type]}
            renderPrompt={renderPrompt}
            attributes={attributes}
          />
        );
      }
      case QUESTION_MATRIX_SINGLE:
      case QUESTION_MATRIX_MULTIPLE: {
        return (
          <MatrixQuestionViewOnly
            question={currentQuestion}
            renderPrompt={renderPrompt}
            attributes={attributes}
          />
        );
      }
      default:
        return <span className="padding-left-xs">{currentQuestion.label}</span>;
    }
  };

  return (
    <Card
      className={classNames(
        'questionnaire-card',
        'overflow-visible',
        'background-grey-1-hover',
        'background-grey-3',
        'border-grey-5',
        'ant-card-padding-none',
        'margin-btm-md',
        'shadow-none',
        'padding-left-xl padding-right-lg padding-top-xxs',
      )}
      data-testid={`question-view-only-${questionIndex}`}
    >
      {renderQuestion(question, nodes)}
    </Card>
  );
};

GenericQuestionViewOnly.propTypes = {
  question: PropTypes.object,
  nodes: PropTypes.array,
  screeningQuestions: PropTypes.array,
  hideActions: PropTypes.bool,
};

export default GenericQuestionViewOnly;
